import { plain } from '../../lib/labels';
import { useState } from 'react';
import {
  Card,
  Table,
  Tag,
  Typography,
  Space,
  Button,
  Modal,
  Input,
  Select,
  App,
  type TableColumnsType,
} from 'antd';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../../api/client';
import { Can } from '../../rbac/RequirePermission';
import { adminLinks } from '../../lib/adminLinks';
import { PersonName } from '../../lib/listenerName';
import { LoadMore, useCursorPages } from '../../lib/useCursorPages';
import { Link } from 'react-router-dom';
import { useUrlState } from '../../lib/urlState';
import { QueryError } from '../../lib/QueryState';

type Severity = 'low' | 'med' | 'high' | 'critical';
type Status = 'pending' | 'actioned' | 'dismissed';
type ActionType = 'warn' | 'mute' | 'suspend' | 'ban' | 'cooldown' | 'no_action';

interface FlagRow {
  id: string;
  targetType: string;
  targetId: string;
  severity: Severity;
  status: Status;
  classifier: string;
  categories: string[];
  createdAt: string;
  reportedUserId?: string | null;
  reportedHandle?: string | null;
  /** Listener display name when the reported account is a listener. */
  reportedDisplayName?: string | null;
  /** The reported account's own user Display Name — absent on older APIs. */
  reportedUserDisplayName?: string | null;
  /** Set when the reported account is a listener. */
  reportedListenerId?: string | null;
  reporterUserId?: string | null;
  /** Who filed the report — absent on older APIs and on automated flags. */
  reporterHandle?: string | null;
  reporterDisplayName?: string | null;
  reporterUserDisplayName?: string | null;
  reporterListenerId?: string | null;
  /** The reporter's free-text note — null when none was written or on older reports. */
  reason?: string | null;
}

/** The reported user, or the reporter, rendered through PersonName; "—" if none. */
function FlagPerson({ row, side }: { row: FlagRow; side: 'reported' | 'reporter' }) {
  const p =
    side === 'reported'
      ? {
          displayName: row.reportedDisplayName,
          userDisplayName: row.reportedUserDisplayName,
          handle: row.reportedHandle,
          userId: row.reportedUserId,
          listenerId: row.reportedListenerId,
        }
      : {
          displayName: row.reporterDisplayName,
          userDisplayName: row.reporterUserDisplayName,
          handle: row.reporterHandle,
          userId: row.reporterUserId,
          listenerId: row.reporterListenerId,
        };
  return p.handle || p.userId ? <PersonName {...p} /> : <span style={{ color: '#8B8B8B' }}>—</span>;
}

interface Page {
  items: FlagRow[];
  nextCursor: string | null;
}

const severityColor: Record<Severity, string> = {
  low: 'default',
  med: 'gold',
  high: 'magenta',
  critical: 'red',
};

export function ModerationPage() {
  const qc = useQueryClient();
  const { message } = App.useApp();
  // ?severity= ?status= (default pending; `all` = no filter) ?q= — kept in the URL.
  const { get, set } = useUrlState();
  const severity = get('severity') ? (get('severity') as Severity) : undefined;
  const setSeverity = (v: Severity | undefined) => set({ severity: v ?? null });
  const statusParam = get('status');
  const status: Status | undefined = statusParam === 'all' ? undefined : statusParam ? (statusParam as Status) : 'pending';
  const setStatus = (v: Status | undefined) => set({ status: v ?? 'all' });
  const [acting, setActing] = useState<FlagRow | null>(null);
  const [actionForm, setActionForm] = useState<{ action: ActionType; reason: string; durationSeconds?: number }>({
    action: 'warn',
    reason: '',
  });

  const q = get('q') ?? '';
  const setQ = (v: string) => set({ q: v || null });
  const list = useCursorPages<FlagRow>(['admin', 'moderation', 'queue', severity, status, q], async (cursor) =>
    (
      await api.get<Page>('/admin/moderation/queue', {
        params: { severity, status, limit: 50, cursor, q: q || undefined },
      })
    ).data,
  );

  const act = useMutation({
    mutationFn: ({ id, body }: { id: string; body: typeof actionForm }) =>
      api.post(`/admin/moderation/${id}/action`, body),
    onSuccess: () => {
      message.success('Action recorded');
      setActing(null);
      setActionForm({ action: 'warn', reason: '' });
      qc.invalidateQueries({ queryKey: ['admin', 'moderation', 'queue'] });
      qc.invalidateQueries({ queryKey: ['admin', 'console', 'attention'] });
    },
    onError: () => message.error('Could not action'),
  });

  const columns: TableColumnsType<FlagRow> = [
    {
      title: 'How serious',
      dataIndex: 'severity',
      render: (s: Severity) => <Tag color={severityColor[s]}>{s.toUpperCase()}</Tag>,
      sorter: (a, b) => severityRank(a.severity) - severityRank(b.severity),
    },
    {
      title: 'Reported user',
      render: (_: unknown, row) => <FlagPerson row={row} side="reported" />,
    },
    {
      title: 'Reported by',
      render: (_: unknown, row) => <FlagPerson row={row} side="reporter" />,
    },
    {
      title: 'About',
      render: (_: unknown, row) => (
        <Space direction="vertical" size={2}>
          <Tag>{plain(row.targetType)}</Tag>
          {/* A flag on a call opens that call. */}
          {/call/i.test(row.targetType) ? (
            <Link to={adminLinks.call(row.targetId)} style={{ fontSize: 11 }}>
              {row.targetId.slice(0, 8)}
            </Link>
          ) : (
            <span style={{ fontSize: 11, color: '#8B8B8B' }}>{row.targetId.slice(0, 8)}</span>
          )}
        </Space>
      ),
    },
    {
      title: 'Categories',
      dataIndex: 'categories',
      render: (c: string[]) => (
        <Space size={[4, 4]} wrap>
          {c.map((cat) => (
            <Tag key={cat}>{plain(cat)}</Tag>
          ))}
        </Space>
      ),
    },
    {
      title: 'Reason',
      dataIndex: 'reason',
      render: (r: string | null | undefined) =>
        r ? (
          <Typography.Paragraph ellipsis={{ rows: 2, tooltip: r }} style={{ margin: 0, maxWidth: 260 }}>
            {r}
          </Typography.Paragraph>
        ) : (
          <span style={{ color: '#8B8B8B' }}>—</span>
        ),
    },
    { title: 'Detected by', dataIndex: 'classifier' },
    {
      title: 'Status',
      dataIndex: 'status',
      render: (s: Status) => (
        <Tag color={s === 'pending' ? 'processing' : s === 'actioned' ? 'success' : 'default'}>{plain(s)}</Tag>
      ),
    },
    {
      title: 'Created',
      dataIndex: 'createdAt',
      render: (s: string) => new Date(s).toLocaleString(),
    },
    {
      title: 'Action',
      render: (_: unknown, row) => (
        <Can permission="moderation.act">
          <Button size="small" type="primary" onClick={() => setActing(row)}>
            Review
          </Button>
        </Can>
      ),
    },
  ];

  return (
    <Space direction="vertical" size="large" style={{ width: '100%' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography.Title level={3} style={{ color: 'white', margin: 0 }}>
          Safety reports
        </Typography.Title>
        <Space>
          <Select<Severity | undefined>
            allowClear
            placeholder="All severities"
            value={severity}
            onChange={setSeverity}
            style={{ minWidth: 160 }}
            options={[
              { value: 'critical', label: 'Critical' },
              { value: 'high', label: 'High' },
              { value: 'med', label: 'Med' },
              { value: 'low', label: 'Low' },
            ]}
          />
          <Select<Status | undefined>
            placeholder="All statuses"
            value={status}
            onChange={setStatus}
            style={{ minWidth: 160 }}
            options={[
              { value: 'pending', label: 'Pending' },
              { value: 'actioned', label: 'Actioned' },
              { value: 'dismissed', label: 'Dismissed' },
            ]}
            allowClear
          />
          <Input.Search
            allowClear
            aria-label="Search flags"
            placeholder="Reported name or @handle"
            key={q}
            defaultValue={q}
            onSearch={(v) => setQ(v.trim())}
            style={{ minWidth: 260 }}
          />
        </Space>
      </div>
      <Card>
        {list.isError && <QueryError error={list.error} what="safety reports" onRetry={list.refetch} style={{ marginBottom: 12 }} />}
        <Table
          rowKey="id"
          loading={list.isLoading}
          dataSource={list.rows}
          columns={columns}
          scroll={{ x: 'max-content' }}
          pagination={{ pageSize: 25, showSizeChanger: true }}
        />
        <LoadMore list={list} noun="flags" />
      </Card>

      <Modal
        title={acting ? `${acting.severity.toUpperCase()} · ${acting.classifier}` : ''}
        open={!!acting}
        onCancel={() => setActing(null)}
        onOk={() => acting && act.mutate({ id: acting.id, body: actionForm })}
        okText="Save action"
        okButtonProps={{
          disabled: actionForm.reason.trim().length < 10,
          loading: act.isPending,
        }}
      >
        {acting && (
          <Space direction="vertical" style={{ width: '100%' }} size={12}>
            <Typography.Text type="secondary">
              {acting.targetType} · {acting.targetId.slice(0, 12)}…
            </Typography.Text>
            <div>
              <Typography.Text type="secondary">Reported user: </Typography.Text>
              <FlagPerson row={acting} side="reported" />
            </div>
            <div>
              <Typography.Text type="secondary">Reported by: </Typography.Text>
              <FlagPerson row={acting} side="reporter" />
            </div>
            <div>
              <Typography.Text type="secondary">Reporter's note: </Typography.Text>
              {acting.reason ? (
                <Typography.Paragraph style={{ margin: '4px 0 0', whiteSpace: 'pre-wrap' }}>{acting.reason}</Typography.Paragraph>
              ) : (
                <span style={{ color: '#8B8B8B' }}>—</span>
              )}
            </div>
            <Select<ActionType>
              style={{ width: '100%' }}
              value={actionForm.action}
              onChange={(action) => setActionForm({ ...actionForm, action })}
              options={[
                { value: 'warn', label: 'Warn' },
                { value: 'mute', label: 'Mute' },
                { value: 'suspend', label: 'Suspend' },
                { value: 'cooldown', label: 'Cooldown' },
                { value: 'ban', label: 'Ban' },
                { value: 'no_action', label: 'Dismiss (no action)' },
              ]}
            />
            {(actionForm.action === 'suspend' || actionForm.action === 'cooldown') && (
              <Input
                type="number"
                placeholder="Duration in seconds (e.g. 86400 for 24h)"
                value={actionForm.durationSeconds ?? ''}
                onChange={(e) =>
                  setActionForm({ ...actionForm, durationSeconds: Number(e.target.value) || undefined })
                }
              />
            )}
            <Input.TextArea
              rows={4}
              value={actionForm.reason}
              onChange={(e) => setActionForm({ ...actionForm, reason: e.target.value })}
              placeholder="Internal note (≥ 10 chars)"
            />
          </Space>
        )}
      </Modal>
    </Space>
  );
}

function severityRank(s: Severity): number {
  return { low: 0, med: 1, high: 2, critical: 3 }[s];
}
